<template>
  <SectionLayout :title="$t('AI_SETTINGS.TITLE')">
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('AI_SETTINGS.LOADING')"
  >
    <template #header>
      <div class="flex justify-between items-center">
        <BaseSettingsHeader
          :description="$t('AI_SETTINGS.SUBTITLE')"
        />
        <woot-button
           v-if="shouldShowWizardButton"
           variant="smooth"
           color-scheme="primary"
           icon="flash-on"
           @click="showWizard = true"
        >
           {{ $t('ONBOARDING_WIZARD.BTN_LABEL') || '⚡ Thiết lập nhanh' }}
        </woot-button>
      </div>
    </template>
    
      <template #body>
        <div class="h-full w-full flex flex-col sm:flex-row gap-4 sm:gap-6">
          <!-- Mobile: Tab manager with dropdown | Tablet+: Left sidebar -->
          <div class="w-full sm:w-64 shrink-0 sm:h-full bg-white dark:bg-slate-800 rounded-2xl shadow-xl sm:overflow-hidden">
            <TabManager v-model:active-tab="activeTab" :tabs="tabs" />
          </div>

          <!-- Mobile: Full width content | Tablet+: Right area -->
          <div class="flex-1 w-full h-full sm:pr-[5px] sm:pb-[5px] overflow-hidden">
            <div class="h-full bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden">
              <section class="h-full min-h-0 overflow-auto p-3 sm:p-4 md:p-6">
          <div v-if="activeTab === 'overview'">
            <overview-tab
              ref="overview"
              :ai-configuration="aiConfiguration"
              :is-loading="isLoading"
              @refresh="loadAiConfiguration"
            />
          </div>

          <div v-if="activeTab === 'business-data' || activeTab.startsWith('business-data:')">
            <business-data-tab
              :key="activeTab"
              :active-tab="activeTab"
              :can-use-business-data="canUseBusinessData"
              :can-use-file-sources="canUseFileSources"
              :can-use-web-importer="canUseWebImporter"
              :can-use-image-library="canUseImageLibrary"
            />
          </div>

          <div v-if="activeTab === 'data-collection'">
            <data-collection-tab />
          </div>

          <div v-if="activeTab === 'summary'">
            <conversation-summary-tab />
          </div>
            </section>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
  <AiOnboardingFlowContainer 
    v-if="canUseAiOnboarding && canUseAiCore"
    :show="showWizard && canUseAiOnboarding && canUseAiCore" 
    @close="showWizard = false"
    @completed="handleOnboardingCompleted"
  />
</SectionLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import { useAlert } from 'dashboard/composables'
import SettingsLayout from '../SettingsLayout.vue'
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue'
import OverviewTab from './components/OverviewTab.vue'
import BusinessDataTab from './components/BusinessDataTab.vue'
import DataCollectionTab from './components/DataCollectionTab.vue'
import ConversationSummaryTab from './components/ConversationSummaryTab.vue'
import TabManager from './components/TabManager.vue'
import AiOnboardingFlowContainer from './aiOnboarding/AiOnboardingFlowContainer.vue'
import SectionLayout from '../../../../components-next/SectionLayout.vue'

export default {
  name: 'AiSettings',
  components: {
    SettingsLayout,
    BaseSettingsHeader,
    OverviewTab,
    BusinessDataTab,
    DataCollectionTab,
    ConversationSummaryTab,
    TabManager,
    SectionLayout,
    AiOnboardingFlowContainer,
  },
  
  data() {
    return {
      activeTab: 'overview',
      aiConfiguration: null,
      providers: [],
      isLoading: true,
      showWizard: false,
      onboardingCompleted: false,
    }
  },
  
  computed: {
    ...mapGetters(['getCurrentAccount', 'getCurrentAccountId', 'accounts/isFeatureEnabledonAccount']),
    currentAccount() {
      return this.getCurrentAccount;
    },
    accountId() {
      return this.getCurrentAccountId;
    },
    isFeatureEnabled() {
      return this['accounts/isFeatureEnabledonAccount'];
    },
    shouldShowWizardButton() {
      if (!this.canUseAiOnboarding || !this.canUseAiCore) return false;
      // Chỉ dựa vào trạng thái ai_onboarding_completed của account
      return !this.onboardingCompleted;
    },
    canUseAiCore() {
      return this.accountId ? this.isFeatureEnabled(this.accountId, 'ai_core') : false;
    },
    canUseAiOnboarding() {
      return this.accountId ? this.isFeatureEnabled(this.accountId, 'ai_onboarding') : false;
    },
    canUseBusinessData() {
      return this.accountId ? this.isFeatureEnabled(this.accountId, 'ai_business_data') : false;
    },
    canUseFileSources() {
      return this.accountId ? this.isFeatureEnabled(this.accountId, 'ai_file_sources') : false;
    },
    canUseWebImporter() {
      return this.accountId ? this.isFeatureEnabled(this.accountId, 'ai_web_importer') : false;
    },
    canUseDataCollection() {
      return this.accountId ? this.isFeatureEnabled(this.accountId, 'ai_data_collection') : false;
    },
    canUseImageLibrary() {
      return this.accountId ? this.isFeatureEnabled(this.accountId, 'ai_image_library') : false;
    },
    canUseConversationSummary() {
      return this.accountId ? this.isFeatureEnabled(this.accountId, 'ai_conversation_summary') : false;
    },
    tabs() {
      if (!this.canUseAiCore) {
        return []
      }
      const tabs = []
      tabs.push({ key: 'overview', name: this.$t('AI_SETTINGS.TABS.OVERVIEW') })
      
      tabs.push({ key: 'business-data:ai-rules', name: this.$t('AI_SETTINGS.TABS.AI_RULES') })

      const knowledgeSubTabs = []
      if (this.canUseBusinessData) {
        knowledgeSubTabs.push({ key: 'business-data:faqs', name: this.$t('AI_SETTINGS.TABS.FAQS') })
        knowledgeSubTabs.push({ key: 'business-data:data-items', name: this.$t('AI_SETTINGS.TABS.DATA_ITEMS') })
      }
      knowledgeSubTabs.push({ key: 'business-data:ai-sample-sentences', name: this.$t('AI_SETTINGS.TABS.AI_SAMPLE_SENTENCES') })
      if (this.canUseImageLibrary) {
        knowledgeSubTabs.push({ key: 'business-data:image-library', name: this.$t('AI_SETTINGS.TABS.IMAGE_LIBRARY') })
      }
      tabs.push({
        key: 'business-data',
        name: this.$t('AI_SETTINGS.TABS.BUSINESS_DATA'),
        subTabs: knowledgeSubTabs,
        defaultActiveKey: knowledgeSubTabs[0]?.key,
        allowParentView: true,
      })


      const dataSourceSubTabs = []
      if (this.canUseFileSources) {
        dataSourceSubTabs.push({ key: 'business-data:file-sources', name: this.$t('AI_SETTINGS.TABS.FILE_SOURCES') })
      }
      if (this.canUseWebImporter) {
        dataSourceSubTabs.push({ key: 'business-data:web-sources', name: this.$t('AI_SETTINGS.TABS.WEB_SOURCES') })
      }
      if (dataSourceSubTabs.length) {
        tabs.push({
          key: 'ai-data-sources',
          name: this.$t('AI_SETTINGS.TABS.AI_DATA_SOURCES'),
          subTabs: dataSourceSubTabs,
          defaultActiveKey: dataSourceSubTabs[0]?.key,
          allowParentView: false,
        })
      }
      if (this.canUseDataCollection) {
        tabs.push({ key: 'data-collection', name: this.$t('AI_SETTINGS.TABS.DATA_COLLECTION') })
      }
      if (this.canUseConversationSummary) {
        tabs.push({ key: 'summary', name: this.$t('AI_SETTINGS.TABS.SUMMARY') })
      }
      return tabs
    }
  },

  watch: {
    canUseAiOnboarding(enabled) {
      if (!enabled) {
        this.showWizard = false;
      }
    },
  },
  
  mounted() {
    this.$nextTick(() => {
      if (this.$store.getters.getCurrentAccountId) {
        this.loadAiConfiguration()
      } else {
        setTimeout(() => {
          if (this.$store.getters.getCurrentAccountId) {
            this.loadAiConfiguration()
          }
        }, 500)
      }
    })
  },
  
  methods: {
    async loadAiConfiguration() {
      try {
        this.isLoading = true
        const accountId = this.$store.getters.getCurrentAccountId
        const response = await window.axios.get(`/api/v1/accounts/${accountId}/ai_configurations`)
        
        if (response.data && response.data !== null) {
          this.aiConfiguration = response.data
        } else {
          this.aiConfiguration = null
        }

        // Luôn kiểm tra trạng thái ai_onboarding_completed của account để quyết định auto-show wizard
        if (this.canUseAiOnboarding && this.canUseAiCore) {
          try {
            const progress = await this.$store.dispatch('aiOnboarding/fetchProgress');
            this.onboardingCompleted = !!progress.completed;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Error loading AI onboarding progress:', e);
            this.onboardingCompleted = false;
          }

          if (!this.onboardingCompleted) {
            const hasSeenOnboarding = localStorage.getItem(`ai_onboarding_seen_${accountId}`);
            if (!hasSeenOnboarding) {
              this.$nextTick(() => {
                this.showWizard = true;
              });
            }
          }
        } else {
          this.onboardingCompleted = false;
        }
      } catch (error) {
        console.error('Error loading AI configuration:', error)
        if (error.response && error.response.status === 404) {
          this.aiConfiguration = null
          // Auto-show onboarding for new users
          if (this.canUseAiOnboarding && this.canUseAiCore) {
            this.$nextTick(() => {
              this.showWizard = true;
            });
          }
        } else if (error.response && error.response.status === 422) {
          const errorMessage = error.response.data?.error || this.$t('AI_SETTINGS.ERRORS.LOAD_FAILED')
          useAlert(errorMessage, {type: 'error'})
        } else {
          useAlert(this.$t('AI_SETTINGS.ERRORS.LOAD_FAILED'), {type: 'error'})
        }
      } finally {
        this.isLoading = false
      }
    },

    handleOnboardingCompleted() {
      this.showWizard = false
      this.onboardingCompleted = true
      
      // Mark that user has seen onboarding
      const accountId = this.$store.getters.getCurrentAccountId
      localStorage.setItem(`ai_onboarding_seen_${accountId}`, 'true');
      
      this.loadAiConfiguration()
      useAlert('🎉 Hoàn tất thiết lập AI Onboarding!', 'success')
    },
  },
}
</script>

 
